import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_check_filled, QIcon_close_default } from '@repo/icons/index'
import { useEffect, useMemo } from 'react'
import { useEventTracking } from '../../../../helpers/gtmHelper'
import { useWeeklyLessonPackageWarning } from '../../../../modules/apis/student/credit'
import { APP_SCREEN_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import useReturnFullUrl from '../../../../modules/hooks/webview/common/useReturnFullUrl'
import useWebviewNavigation from '../../../../modules/hooks/webview/common/useWebviewNavigation'
import { n_book_lessons } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import Etiquette from '../../../Etiquette'
import ScheduleItem from './ScheduleItem'

const CompletedLesson = ({ result }) => {
  const { t } = useTranslation()
  const { results, is_first } = result.data
  const schedules = result.schedules
  const isWebview = useIsWebview()
  const isFirstLesson = is_first
  const router = useAppRouter()
  const getFullUrl = useReturnFullUrl()
  const { sendPageViewEvent, sendClickEvent } = useEventTracking()
  const isSchedule = useMemo(
    () => router.asPath.includes('/schedule') || router.asPath.includes('/tutors/'),
    [router.asPath]
  )
  const { data: weeklyLessonPackageData } = useWeeklyLessonPackageWarning()
  const lesson = results[0]?.lesson

  useEffect(() => {
    if (!router) return
    sendPageViewEvent({ page_name: 'book_lesson_complete' })
  }, [router.asPath])

  useEffect(() => {
    if (!router) return
    if (router.asPath.includes('/portal/lessons/schedule')) {
      sendPageViewEvent({ page_name: 'book_lesson_complete_for_schedule' })
    }
  }, [router.asPath])

  useEffect(() => {
    if (window) {
      window.scrollTo(0, 0)
    }
  }, [])

  const { popBackToRingleApp } = useWebviewNavigation()

  return (
    <div className="relative">
      {isWebview && (
        <div className="px-16 w-full flex justify-end">
          <button
            onClick={() => {
              popBackToRingleApp()
            }}
          >
            <QIcon_close_default width={24} height={24} />
          </button>
        </div>
      )}
      <div className="mx-auto flex w-full flex-col items-center justify-center px-[16px] pt-[32px] sm:mt-[40px] sm:max-w-[336px] sm:px-[0px]">
        <QIcon_check_filled color="#2BAB63" width={64} height={64} />
        <div className="Headline2-24Bd mt-[16px] text-center">{t(n_book_lessons.complete_lesson.title)}</div>
        <div className="flex w-full flex-col gap-y-[16px] my-[24px]">
          {schedules.map((schedule, i) => {
            return (
              <>
                <ScheduleItem schedule={schedule} key={`complete-schedule-${i}`} />
                <div className="w-full sm:mt-[16px]">
                  <DSButton
                    type={BUTTON_TYPE.OUTLINE}
                    size={BUTTON_SIZE.MD}
                    customBorderColor="#3C2BAC"
                    onClick={() => {
                      sendClickEvent({
                        button_name: 'lesson_select_material',
                      })

                      if (isWebview) {
                        if (lesson) {
                          if (lesson?.course_id) {
                            router.push(urls.student.newPortal.lessons.upcomingLessons, {
                              appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                              appSendData: { url: getFullUrl(urls.webview.lessons.index) },
                            })
                          } else {
                            router.push(urls.student.newPortal.lessons.materialSelect(lesson.id), {
                              appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                              appSendData: {
                                url: getFullUrl(
                                  urls.webview.lessons.materialSelect(lesson.id, lesson.course_id, 'schedule')
                                ),
                              },
                            })
                          }
                        } else {
                          router.appScreenReset({
                            appScreenName: APP_SCREEN_NAME.PortalHome,
                          })
                        }
                      } else {
                        if (lesson) {
                          router.push(urls.student.newPortal.lessons.materialSelect(lesson.id))
                        } else {
                          router.push(urls.student.newPortal.lessons.upcomingLessons, {
                            appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                            appSendData: { url: getFullUrl(urls.webview.lessons.index) },
                          })
                        }
                      }
                    }}
                  >
                    <DSButton.Label customTxtColor="#3C2BAC">
                      {lesson?.course_id
                        ? t(n_book_lessons.complete_lesson.btn_lesson_prep)
                        : t(n_book_lessons.complete_lesson.btn_prepare)}
                    </DSButton.Label>
                  </DSButton>
                </div>
              </>
            )
          })}
        </div>
        <div className="w-full h-[1px] mb-[24px] bg-gray-200" />
        <div className="fixed bottom-0 left-0 grid w-full grid-cols-1 whitespace-nowrap px-[16px] sm:px-[0px] pb-[40px] sm:pb-[24px] pt-[12px] sm:pt-[0px] sm:relative bg-white shadow-[0px_2px_8px_0px_rgba(0,0,0,0.15)] sm:shadow-none">
          {weeklyLessonPackageData ? (
            <div>
              <div className="Body2-14Bd text-center">{t(n_book_lessons.complete_lesson.book_more_caption_2)}</div>
              <div className="Body1-16Bd text-center">{t(n_book_lessons.complete_lesson.book_more_caption_3)}</div>
            </div>
          ) : (
            <div className="Body1-16Bd text-center">{t(n_book_lessons.complete_lesson.book_more_caption)}</div>
          )}
          <div className="w-full mt-[8px]">
            <DSButton
              type={BUTTON_TYPE.SOLID}
              size={BUTTON_SIZE.MD}
              onClick={() => {
                sendClickEvent({
                  button_name: 'book_more',
                })
                isSchedule
                  ? window.location.reload()
                  : router.push(urls.student.newPortal.lessons.schedule, {
                      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                      appSendData: {
                        url: getFullUrl(urls.webview.lessons.schedule),
                      },
                    })
              }}
            >
              <DSButton.Label>{t(n_book_lessons.complete_lesson.btn_book_more)}</DSButton.Label>
            </DSButton>
          </div>
        </div>
        <div className="w-full">
          <div className="Body2-14Bd">{t(n_book_lessons.complete_lesson.guide_title)}</div>
          <div className="Body2-14Md mt-[4px] whitespace-pre-line text-gray-600">
            {t(n_book_lessons.complete_lesson.guide_content)}
          </div>
        </div>
        <div className="h-[160px] sm:hidden" />
      </div>
      {isFirstLesson && (
        <div className="mt-[38px] w-full">
          <Etiquette />
        </div>
      )}
    </div>
  )
}
export default CompletedLesson
